// Wave 20.13 — Search + multi-select of aero.chat users, used
// inside the Drop share dialog for in-app sending.
//
// Debounced 300ms live search against GET /users/search?q=. Selected
// users are chip-rendered above the input; tap the × on a chip to
// remove. Enter on a matching row selects it.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { colors } from '../theme';

const API = process.env.REACT_APP_API || 'https://api.aero.chat';
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';

export interface UserOption {
  id: number;
  username: string;
  displayName: string;
}

interface Props {
  selected: UserOption[];
  onChange: (users: UserOption[]) => void;
  placeholder?: string;
}

export default function UserPicker({ selected, onChange, placeholder }: Props) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<UserOption[]>([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef<any>(null);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const jwt = localStorage.getItem(TOKEN_KEY);
        const res = await fetch(
          `${API}/users/search?q=${encodeURIComponent(q.trim())}`,
          { headers: jwt ? { Authorization: `Bearer ${jwt}` } : undefined },
        );
        const data = res.ok ? await res.json() : [];
        setResults(Array.isArray(data) ? data : []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  const selectedIds = useMemo(() => new Set(selected.map((u) => u.id)), [selected]);
  const filtered = results.filter((r) => !selectedIds.has(r.id));

  const add = (u: UserOption) => {
    onChange([...selected, u]);
    setQ('');
    setResults([]);
  };
  const remove = (id: number) => onChange(selected.filter((u) => u.id !== id));

  return (
    <Box>
      {selected.length > 0 && (
        <Stack direction="row" spacing={0.75} sx={{ mb: 1, flexWrap: 'wrap', gap: 0.75 }}>
          {selected.map((u) => (
            <Chip
              key={u.id}
              label={u.displayName || u.username}
              onDelete={() => remove(u.id)}
              size="small"
              sx={{
                backgroundColor: `${colors.brand}22`,
                color: colors.text,
                fontWeight: 700,
              }}
            />
          ))}
        </Stack>
      )}
      <TextField
        fullWidth
        size="small"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={placeholder ?? 'Search by name or username'}
        InputProps={{
          endAdornment: searching ? <CircularProgress size={16} /> : null,
        }}
      />
      {filtered.length > 0 && (
        <Box
          sx={{
            mt: 0.5,
            maxHeight: 180,
            overflowY: 'auto',
            border: `1px solid ${colors.border}`,
            borderRadius: 1,
            backgroundColor: colors.surface,
          }}
        >
          <List dense disablePadding>
            {filtered.map((u) => (
              <ListItemButton key={u.id} onClick={() => add(u)}>
                <ListItemText
                  primary={u.displayName || u.username}
                  secondary={u.username ? `@${u.username}` : undefined}
                  primaryTypographyProps={{ fontWeight: 700, color: colors.text }}
                  secondaryTypographyProps={{ color: colors.textMuted }}
                />
              </ListItemButton>
            ))}
          </List>
        </Box>
      )}
      {q.trim().length >= 2 && !searching && filtered.length === 0 && (
        <Typography sx={{ mt: 0.5, color: colors.textMuted, fontSize: '0.8rem' }}>
          No matches. Ask them to sign up at aero.chat.
        </Typography>
      )}
    </Box>
  );
}
